import { makeAutoObservable } from "mobx";
import { ProductAudit, AuditRequest } from "../../models/ProductAudit";
import { productAuditApi } from "../../apis/ProductAudit";

export class ProductAuditPageModel {
    candidates: ProductAudit[] = [];
    processed: ProductAudit[] = [];
    selectedAudit: ProductAudit | null = null;
    currentImageIndex: number = 0;
    showRefuseDialog: boolean = false;
    showConfirmDialog: boolean = false;
    refuseReason: string = "";
    loading: boolean = false;

    constructor() {
        makeAutoObservable(this);
    }

    async loadCandidates() {
        this.loading = true;
        try {
            const list = await productAuditApi.getAllCandidates();
            // 待审接口返回的就是待审单；兜底补 draftStatus，避免按钮被藏掉
            this.candidates = (list || []).map(item => ({
                ...item,
                draftStatus: item.draftStatus ?? 2,
            }));
        } finally {
            this.loading = false;
        }
    }

    async loadProcessed() {
        this.loading = true;
        try {
            this.processed = await productAuditApi.getAllProcessed();
        } finally {
            this.loading = false;
        }
    }

    setSelectedAudit(audit: ProductAudit | null) {
        this.selectedAudit = audit;
        this.currentImageIndex = 0;
    }

    nextImage() {
        if (this.selectedAudit && this.selectedAudit.headImages && this.currentImageIndex < this.selectedAudit.headImages.length - 1) {
            this.currentImageIndex++;
        }
    }

    previousImage() {
        if (this.currentImageIndex > 0) {
            this.currentImageIndex--;
        }
    }

    setShowRefuseDialog(show: boolean) {
        this.showRefuseDialog = show;
        if (!show) {
            this.refuseReason = "";
        }
    }

    setShowConfirmDialog(show: boolean) {
        this.showConfirmDialog = show;
    }

    setRefuseReason(reason: string) {
        this.refuseReason = reason;
    }

    /** 仅 draftStatus=2（审核中）可审；不能靠“是否出现在已审列表”判断 */
    canAuditSelected(): boolean {
        if (!this.selectedAudit) return false;
        if (this.selectedAudit.draftStatus === 2) return true;
        return this.candidates.some(item => item.draftId === this.selectedAudit?.draftId);
    }

    isSelectedAuditProcessed(): boolean {
        return !this.canAuditSelected();
    }

    async approveAudit() {
        if (!this.selectedAudit) return;
        
        const request: AuditRequest = {
            draftId: this.selectedAudit.draftId,
            approved: true
        };

        this.loading = true;
        try {
            await productAuditApi.productAudit(request);
            await this.loadCandidates();
            await this.loadProcessed();
            this.setSelectedAudit(null);
            this.setShowConfirmDialog(false);
        } finally {
            this.loading = false;
        }
    }

    async refuseAudit() {
        if (!this.selectedAudit || !this.refuseReason) return;

        const request: AuditRequest = {
            draftId: this.selectedAudit.draftId,
            approved: false,
            refuseReason: this.refuseReason
        };

        this.loading = true;
        try {
            await productAuditApi.productAudit(request);
            await this.loadCandidates();
            await this.loadProcessed();
            this.setSelectedAudit(null);
            this.setShowRefuseDialog(false);
        } finally {
            this.loading = false;
        }
    }
} 